import React from "react";
import AppShell from "@/components/AppShell";
import ProfileAvatar from "@/components/ProfileAvatar";
import FounderBadge from "@/components/FounderBadge";
import { useAuth } from "@/context/AuthContext";
import { Mail, Crown, ShieldCheck, Coins, Sparkle } from "lucide-react";

/**
 * The Profile page — identity at a glance. Prominently highlights Founder status with the
 * Maison Gold palette when the account is the Root Admin (Supreme Director).
 */
const Profile = () => {
  const { user } = useAuth();
  const isFounder = !!user?.is_root_admin;

  return (
    <AppShell>
      <div className="mb-8">
        <h1 className="font-serif text-4xl mb-1">My Profile</h1>
        <p className="text-muted-foreground">Your identity within the Gifted Estate.</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Identity card */}
        <div className="glass rounded-[2rem] p-8 flex items-center gap-5" data-testid="profile-identity-card">
          <ProfileAvatar name={user?.name} sizeClass="w-20 h-20" textClass="text-3xl" isRootAdmin={isFounder} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-serif text-2xl truncate">{user?.name}</h2>
              {isFounder && <FounderBadge size="sm" />}
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
              <Mail className="w-3.5 h-3.5" /> {user?.email}
            </div>
          </div>
        </div>

        {/* Sovereign Founder panel — Maison Gold palette, Root Admin only */}
        {isFounder && (
          <div
            data-testid="founder-panel"
            className="rounded-[2rem] p-8 text-center relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(246,227,163,0.18) 0%, rgba(201,162,74,0.14) 50%, rgba(138,106,42,0.10) 100%)",
              border: "1px solid rgba(201,162,74,0.4)",
            }}
          >
            <div
              className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
              style={{ background: "linear-gradient(135deg, #f6e3a3 0%, #c9a24a 50%, #8a6a2a 100%)" }}
            >
              <Crown className="w-8 h-8" style={{ color: "#3d2e0f" }} />
            </div>
            <h3
              className="text-3xl italic font-semibold mb-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#8a6a2a" }}
            >
              Sovereign Founder
            </h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
              Supreme Director access — every Goliath &amp; Royal feature unlocked without a personal
              transaction, the encrypted Maison Evolution archive, and a direct view of the Lob
              fulfillment queue.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 text-left">
              <div className="surface rounded-2xl p-4">
                <Sparkle className="w-4 h-4 gold-text mb-2" />
                <div className="text-sm font-medium">Maison Evolution</div>
                <div className="text-xs text-muted-foreground">Encrypted build archive</div>
              </div>
              <div className="surface rounded-2xl p-4">
                <ShieldCheck className="w-4 h-4 gold-text mb-2" />
                <div className="text-sm font-medium">Tier Override</div>
                <div className="text-xs text-muted-foreground">Every premium feature, unlocked</div>
              </div>
              <div className="surface rounded-2xl p-4">
                <Coins className="w-4 h-4 gold-text mb-2" />
                <div className="text-sm font-medium">Fulfillment View</div>
                <div className="text-xs text-muted-foreground">Live Lob mailing queue</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default Profile;
